import Vue from 'vue';
import { Guid } from 'guid-typescript';
import api from '@/utils/api';

export interface DataTableOptions<T> {
  trackBy?: keyof T | string;
  perPage?: number;
  throttleMs?: number;
  timeout?: number; // Axios request timeout in ms (overrides global default)
  initialRows?: T[];
  server?: boolean; // enable server-side pagination/search/sort
  // Optional hook to build query params. Receives state and returns object merged into request.
  buildQueryParams?: (state: {
    page: number;
    perPage: number;
    search: string;
    sort: SortState | null;
  }) => Record<string, any>;
  // Optional hook to parse response, must return { rows, total }
  parseResponse?: (resp: any) => { rows: T[]; total: number };
}

export interface SortState { field: string; direction: 'asc' | 'desc'; }

export interface DataTableApi<T> extends Record<string, any> {
  rows: T[];
  filteredRows: T[];
  pageRows: T[];
  loading: boolean;
  selection: Set<string | number>;
  bulkSelection: Set<string | number>;
  pagination: { page: number; perPage: number; total: number; pageCount: number };
  sortState: SortState | null;
  searchText: string;
  load: (url: string) => Promise<void>;
  setRows: (rows: T[]) => void;
  applySearch: (text: string) => void;
  sort: (field: string, direction: 'asc' | 'desc') => void;
  toggleBulk: (id: string | number) => void;
  bulkSelectAll: () => void;
  isBulkAllSelected: () => boolean;
  toggleSelect: (id: string | number) => void;
  duplicateRow: (id: string | number) => T | undefined;
  setPage: (page: number) => void;
  clearSearch: () => void;
}

export function useDataTable<T extends Record<string, any>>(options: DataTableOptions<T> = {}): DataTableApi<T> {
  const trackBy = options.trackBy || 'id';
  const perPage = options.perPage || 20;
  const throttleMs = options.throttleMs || 250;

  let throttleHandle: number | null = null;

  function identifier(row: T): string | number {
    const raw = (row as any)[trackBy];
    return typeof raw === 'string' || typeof raw === 'number' ? raw : JSON.stringify(raw);
  }

  function deepMatch(obj: any, text: string): boolean {
    if (obj == null) return false;
    if (typeof obj === 'string' || typeof obj === 'number') {
      return obj.toString().toUpperCase().includes(text);
    }
    if (Array.isArray(obj)) return obj.some(el => deepMatch(el, text));
    if (typeof obj === 'object') return Object.values(obj).some(v => deepMatch(v, text));
    return false;
  }

  const serverMode = !!options.server;
  let lastUrl: string | null = null;

  const apiObj: DataTableApi<T> = Vue.observable({
    rows: options.initialRows ? [...options.initialRows] : [],
    // Explicit boolean widening to avoid literal type inference causing 'true not assignable to false'
    loading: false as boolean,
    selection: new Set<string | number>(),
    bulkSelection: new Set<string | number>(),
    sortState: null as SortState | null,
    searchText: '',
    pagination: { page: 1, perPage, total: 0, pageCount: 0 },
    get filteredRows() {
      // In server mode we trust server-side filtering & sorting.
      if (serverMode) return this.rows;
      let base = [...this.rows];
      if (this.sortState) {
        const { field, direction } = this.sortState;
        const getFieldValue = (obj: any, path: string) => {
          if (!path.includes('.')) return obj ? obj[path] : undefined;
          return path.split('.').reduce((acc: any, part: string) => (acc ? acc[part] : undefined), obj);
        };
        base.sort((a, b) => {
          const av = getFieldValue(a, field);
          const bv = getFieldValue(b, field);
          if (av == null && bv == null) return 0;
          if (av == null) return direction === 'asc' ? -1 : 1;
          if (bv == null) return direction === 'asc' ? 1 : -1;
          if (typeof av === 'number' && typeof bv === 'number') {
            return direction === 'asc' ? av - bv : bv - av;
          }
          return direction === 'asc'
            ? av.toString().localeCompare(bv?.toString())
            : bv?.toString().localeCompare(av.toString());
        });
      }
      const text = this.searchText.trim().toUpperCase();
      if (!text) return base;
      return base.filter(r => deepMatch(r, text));
    },
    get pageRows() {
      if (serverMode) {
        // Server already returned current page rows; pageCount & total updated in load.
        return this.rows;
      }
      this.pagination.total = this.filteredRows.length;
      this.pagination.pageCount = Math.max(1, Math.ceil(this.pagination.total / this.pagination.perPage));
      if (this.pagination.page > this.pagination.pageCount) this.pagination.page = this.pagination.pageCount;
      const start = (this.pagination.page - 1) * this.pagination.perPage;
      return this.filteredRows.slice(start, start + this.pagination.perPage);
    },
    load(url: string) {
      this.loading = true;
      lastUrl = url;
      const reqConfig = options.timeout ? { timeout: options.timeout } : {};
      if (!serverMode) {
        return api.get(url, reqConfig)
          .then(resp => {
            if (resp.data) {
              this.rows = resp.data;
              this.pagination.page = 1;
            }
          })
          .finally(() => { this.loading = false; });
      } else {
        const sortCombined = this.sortState ? `${this.sortState.field}|${this.sortState.direction}` : undefined;
        const queryParams = options.buildQueryParams
          ? options.buildQueryParams({
              page: this.pagination.page,
              perPage: this.pagination.perPage,
              search: this.searchText,
              sort: this.sortState,
            })
          : {
              // Backend expected naming: page, per_page, search, sort ("field|direction")
              page: this.pagination.page,
              per_page: this.pagination.perPage,
              search: this.searchText || undefined,
              sort: sortCombined,
            };
        // build query string
        const qs = Object.entries(queryParams)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(String(v)))
          .join('&');
        const fullUrl = qs ? (url + (url.includes('?') ? '&' : '?') + qs) : url;
        return api.get(fullUrl, reqConfig)
          .then(resp => {
            const parsed = options.parseResponse
              ? options.parseResponse(resp)
              : defaultParse(resp);
            this.rows = parsed.rows;
            this.pagination.total = parsed.total;
            this.pagination.pageCount = Math.max(1, Math.ceil(parsed.total / this.pagination.perPage));
          })
          .finally(() => { this.loading = false; });
      }
    },
    setRows(rows: T[]) {
      this.rows = [...rows];
      this.pagination.page = 1;
    },
    applySearch(text: string) {
      if (throttleHandle) window.clearTimeout(throttleHandle);
      throttleHandle = window.setTimeout(() => {
        this.searchText = text;
        this.pagination.page = 1;
        if (serverMode && lastUrl) this.load(lastUrl);
      }, throttleMs);
    },
    clearSearch() {
      this.searchText = '';
      this.pagination.page = 1;
      if (serverMode && lastUrl) this.load(lastUrl);
    },
    sort(field: string, direction: 'asc' | 'desc') {
      this.sortState = { field, direction };
      this.pagination.page = 1;
      if (serverMode && lastUrl) this.load(lastUrl);
    },
    toggleBulk(id: string | number) {
      const bs = new Set(this.bulkSelection);
      if (bs.has(id)) bs.delete(id); else bs.add(id);
      this.bulkSelection = bs; // reassign to trigger reactivity
    },
    bulkSelectAll() {
      // act on current page rows (visible rows) to mimic legacy behavior
      const visible = serverMode ? this.rows : this.pageRows;
      if (visible.length && visible.every(r => this.bulkSelection.has(identifier(r)))) {
        // all selected -> clear all those ids
        const remaining = new Set(this.bulkSelection);
        visible.forEach(r => remaining.delete(identifier(r)));
        this.bulkSelection = remaining;
      } else {
        const bs = new Set(this.bulkSelection);
        visible.forEach(r => bs.add(identifier(r)));
        this.bulkSelection = bs;
      }
    },
    isBulkAllSelected() {
      const visible = serverMode ? this.rows : this.pageRows;
      return !!visible.length && visible.every(r => this.bulkSelection.has(identifier(r)));
    },
    toggleSelect(id: string | number) {
      if (this.selection.has(id)) this.selection.delete(id); else this.selection.add(id);
    },
    duplicateRow(id: string | number) {
      const idx = this.rows.findIndex(r => identifier(r) === id);
      if (idx === -1) return undefined;
      const original = this.rows[idx];
      const clone: T = JSON.parse(JSON.stringify(original));
      if (typeof (clone as any)[trackBy] === 'string') {
        (clone as any)[trackBy] = Guid.create().toString();
      }
      this.rows.splice(idx + 1, 0, clone);
      return clone;
    },
    setPage(page: number) {
      if (page < 1) page = 1;
      if (page > this.pagination.pageCount) page = this.pagination.pageCount;
      this.pagination.page = page;
      if (serverMode && lastUrl) this.load(lastUrl);
    },
  });

  function defaultParse(resp: any): { rows: T[]; total: number } {
    const d = resp.data;
    // Common shapes: { items: [], total } OR { items: [], totalCount } OR { data: { items: [], totalCount } } OR array
    if (Array.isArray(d)) return { rows: d, total: d.length };
    if (d) {
      // Shape: { items: [], total? / totalCount? / count? }
      if (Array.isArray(d.items)) {
        const total = d.totalCount ?? d.total ?? d.count ?? d.items.length;
        return { rows: d.items, total };
      }
      // Shape: { data: { items: [], totalCount? / total? } }
      if (d.data && Array.isArray(d.data.items)) {
        const total = d.data.totalCount ?? d.data.total ?? d.data.count ?? d.totalCount ?? d.total ?? d.count ?? d.data.items.length;
        return { rows: d.data.items, total };
      }
      // Shape: { data: [], total? }
      if (Array.isArray(d.data)) {
        const total = d.totalCount ?? d.total ?? d.count ?? d.data.length;
        return { rows: d.data, total };
      }
      // Fallback: find first array value
      const firstArray = Object.values(d).find(v => Array.isArray(v)) as any[] | undefined;
      if (firstArray) {
        const total = d.totalCount ?? d.total ?? d.count ?? firstArray.length;
        return { rows: firstArray as T[], total };
      }
    }
    return { rows: [], total: 0 };
  }

  return apiObj;
}
